"use client";
import { getItem, removeItem, setItem } from "../libs/encryptedStorage";

/**
 * God Mode — client-side session state.
 *
 * The operator's session lives in local storage next to the normal login state
 * so a page refresh keeps it. The session id is only a pointer: every God Mode
 * request is re-validated server-side against the operator's Auth0 token, so a
 * tampered value here buys nothing.
 */

export const GOD_MODE_STORAGE_KEY = "godModeSession";
export const GOD_MODE_SESSION_HEADER = "x-god-mode-session";
export const GOD_MODE_TEACHER_PREFIX = "SUPPORT_";

/**
 * Height of the fixed God Mode banner, in pixels.
 *
 * The dashboard pins its chrome to absolute offsets from the viewport top
 * (.topbar-container at top:0, the sider at top:56, content at marginTop:56), so
 * the banner cannot simply take up flow space — it is fixed, and the layout adds
 * this value to those offsets while a session is open. Both sides must agree on
 * one number, so it lives here rather than being written twice.
 */
export const GOD_MODE_BANNER_HEIGHT = 44;

export const GodModeMode = {
    READ_ONLY: "READ_ONLY",
    WRITE: "WRITE",
} as const;

export interface GodModeSession {
    sessionId: string;
    centerId: string;
    centerName?: string;
    mode: string;
    reason?: string;
    startedOn: number;
    expiresOn: number;
}

export function getGodModeSession(): GodModeSession | null {
    try {
        const raw = getItem(GOD_MODE_STORAGE_KEY);
        if (!raw) return null;
        const session: GodModeSession = JSON.parse(raw);
        if (!session?.sessionId) return null;
        // Expired sessions are dropped locally too, so the banner and the write
        // guard don't keep claiming a session the server has already stopped
        // honouring.
        if (session.expiresOn && session.expiresOn <= Date.now()) {
            removeItem(GOD_MODE_STORAGE_KEY);
            return null;
        }
        return session;
    } catch (err) {
        console.log("[godMode] Failed to read session", err);
        return null;
    }
}

export function setGodModeSession(session: GodModeSession): void {
    setItem(GOD_MODE_STORAGE_KEY, session);
}

export function clearGodModeSession(): void {
    removeItem(GOD_MODE_STORAGE_KEY);
}

/**
 * The God Mode header, for whichever HTTP helper is making the call.
 *
 * This exists as a shared function rather than an inline object because Bookingo
 * has more than one callApi — superAgentUtil and accountSuperAgentUtil — and the
 * second one was missed when God Mode was first wired up, which left bookings and
 * payments writable during a read-only session (BOOK-590). Any new caller should
 * spread this rather than writing the header name out again.
 */
export function godModeHeaders(): Record<string, string> {
    const session = getGodModeSession();
    return session?.sessionId ? { [GOD_MODE_SESSION_HEADER]: session.sessionId } : {};
}

export function isGodModeActive(): boolean {
    return !!getGodModeSession();
}

export function isGodModeReadOnly(): boolean {
    const session = getGodModeSession();
    return !!session && session.mode !== GodModeMode.WRITE;
}

/** True for the synthetic support teacher — used to skip staff-only side effects
 *  such as writing lastLogin, which would create a /teachers row. */
/**
 * May the current session create bookings dated in the past?
 *
 * Only a God Mode session that has taken control. Normal staff keep the existing
 * "no past dates" rule, and a read-only session cannot write at all — offering
 * past dates there would just produce a picker whose selections are refused.
 *
 * The server has never rejected a past startDate (the booking schema takes a plain
 * epoch), so this is the whole gate: it is a UI affordance, not an enforcement
 * boundary. Treat it that way if a caller ever needs the opposite guarantee.
 */
export function canBackdateBookings(): boolean {
    const session = getGodModeSession();
    return !!session && session.mode === GodModeMode.WRITE;
}

export function isSupportTeacher(teacher: any): boolean {
    return !!teacher && (teacher.godMode === true ||
        (typeof teacher.id === "string" && teacher.id.startsWith(GOD_MODE_TEACHER_PREFIX)));
}

export function getMillisecondsRemaining(): number {
    const session = getGodModeSession();
    if (!session?.expiresOn) return 0;
    return Math.max(0, session.expiresOn - Date.now());
}

/**
 * Client-side write guard.
 *
 * This is an accident guard, not a security boundary — the real enforcement is
 * middleware/godMode.ts on the API, which refuses every mutating request while a
 * session is read-only. This exists so the UI can fail fast and explain itself
 * instead of firing a request that comes back 403.
 */
export function assertGodModeWritable(action = "This action"): boolean {
    if (!isGodModeReadOnly()) return true;
    console.warn(`[godMode] ${action} blocked: session is read-only`);
    return false;
}
